import { Link } from '@inertiajs/react'
import {
  Blocks,
  CreditCard,
  DollarSign,
  Gift,
  LayoutGrid,
  LogOut,
  Package,
  PanelLeftClose,
  PanelLeftOpen,
  Shield,
  SlidersHorizontal,
  Trophy,
  Users,
  type LucideIcon,
} from 'lucide-react'
import { router } from '@inertiajs/react'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'

export type NavigationPermissions = {
  cards?: boolean
  members?: boolean
  roles?: boolean
  versions?: boolean
}

export interface NavItem {
  label: string
  href: string
  icon: LucideIcon
  activePrefix?: string
  badge?: string
  permission?: keyof NavigationPermissions
}

export const NAV_SECTIONS: { title: string; items: NavItem[] }[] = [
  {
    title: 'Workspace',
    items: [
      { label: 'Dashboard', href: '/', icon: LayoutGrid },
      { label: 'Cards', href: '/cards', icon: CreditCard, activePrefix: '/cards', permission: 'cards' },
    ],
  },
  {
    title: 'Game & Economy',
    items: [
      { label: 'Overview', href: '/overview', icon: LayoutGrid, activePrefix: '/overview' },
      { label: 'Players', href: '/players', icon: Users, activePrefix: '/players' },
      { label: 'Game Config', href: '/game-config', icon: SlidersHorizontal, activePrefix: '/game-config' },
      { label: 'Leaderboard', href: '/leaderboard', icon: Trophy, activePrefix: '/leaderboard' },
      { label: 'IAP Revenue', href: '/revenue', icon: DollarSign, activePrefix: '/revenue' },
      { label: 'Gift Codes', href: '/gift-codes', icon: Gift, activePrefix: '/gift-codes' },
      {
        label: 'Versions',
        href: '/versions',
        icon: Package,
        activePrefix: '/versions',
        badge: 'v2.4',
        permission: 'versions',
      },
    ],
  },
  {
    title: 'Development',
    items: [{ label: 'UI Components', href: '/ui', icon: Blocks, activePrefix: '/ui' }],
  },
  {
    title: 'Administration',
    items: [
      { label: 'Members', href: '/members', icon: Users, activePrefix: '/members', permission: 'members' },
      { label: 'Roles', href: '/roles', icon: Shield, activePrefix: '/roles', permission: 'roles' },
    ],
  },
]

interface AppSidebarProps {
  url: string
  collapsed: boolean
  onToggleCollapse: () => void
  navigation: NavigationPermissions
  user?: { name?: string; email: string }
}

function initials(user?: { name?: string; email: string }) {
  const source = user?.name || user?.email || '?'
  return source.slice(0, 2).toUpperCase()
}

export function AppSidebar({
  url,
  collapsed,
  onToggleCollapse,
  navigation,
  user,
}: AppSidebarProps) {
  /* The longest matching prefix wins, so /cards/12/edit lights up Cards rather
   * than both Cards and Dashboard. */
  const all = NAV_SECTIONS.flatMap((s) => s.items).filter(
    (item) => !item.permission || navigation[item.permission],
  )
  const matches = all.filter((item) => url.startsWith(item.activePrefix ?? item.href))
  const activeHref = matches.length
    ? matches.reduce((a, b) =>
        (a.activePrefix ?? a.href).length >= (b.activePrefix ?? b.href).length ? a : b,
      ).href
    : '/'

  return (
    <aside
      /* The width snaps. Animating it costs a layout pass every frame and, in
       * this flex shell, reflows the page beside it; the labels fade in on
       * their own once the width has landed. */
      className={cn(
        'border-border bg-bg sticky top-0 flex h-screen shrink-0 flex-col border-r p-3 select-none',
        collapsed ? 'w-16' : 'w-64',
      )}
    >
      <div className="mb-3 flex h-9 items-center gap-2">
        {collapsed ? (
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleCollapse}
            aria-label="Expand sidebar"
            className="group hover:bg-bg-subtle mx-auto rounded-lg"
          >
            <span
              aria-hidden
              className="bg-fg text-2xs text-bg grid size-7 place-items-center rounded-md font-semibold group-hover:hidden group-focus-visible:hidden"
            >
              RH
            </span>
            <PanelLeftOpen className="text-fg hidden group-hover:block group-focus-visible:block" />
          </Button>
        ) : (
          <>
            <span
              aria-hidden
              className="bg-fg text-2xs text-bg grid size-7 shrink-0 place-items-center rounded-md font-semibold"
            >
              RH
            </span>
            <span className="text-fg truncate text-sm font-semibold tracking-tight">Roundhouse</span>
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggleCollapse}
              aria-label="Collapse sidebar"
              className="ml-auto shrink-0 rounded-lg"
            >
              <PanelLeftClose />
            </Button>
          </>
        )}
      </div>

      <nav
        aria-label="Main"
        className="scrollbar-none flex flex-1 flex-col gap-4 overflow-y-auto overscroll-contain"
      >
        {NAV_SECTIONS.map((section) => {
          const items = section.items.filter(
            (item) => !item.permission || navigation[item.permission],
          )
          if (items.length === 0) return null

          return (
            <div key={section.title} className="flex flex-col gap-0.5">
              {!collapsed && <div className="t-label px-3 pt-2 pb-1">{section.title}</div>}

              {items.map((item) => {
                const Icon = item.icon
                const active = item.href === activeHref
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    aria-label={collapsed ? item.label : undefined}
                    title={collapsed ? item.label : undefined}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'flex h-9 items-center gap-3 rounded-lg text-sm outline-none',
                      'transition-colors duration-100 ease-out',
                      'focus-visible:outline-accent focus-visible:outline-2 focus-visible:outline-offset-1',
                      collapsed ? 'w-9 justify-center self-center px-0' : 'px-3',
                      active
                        ? 'bg-bg-hover text-fg font-medium'
                        : 'text-fg-muted hover:bg-bg-subtle hover:text-fg',
                    )}
                  >
                    <Icon aria-hidden className="size-4 shrink-0" />
                    {!collapsed && (
                      <>
                        <span className="animate-in fade-in slide-in-from-left-1 truncate duration-100">
                          {item.label}
                        </span>
                        {item.badge && (
                          <Badge variant="outline" size="tag" className="text-fg-subtle ml-auto">
                            {item.badge}
                          </Badge>
                        )}
                      </>
                    )}
                  </Link>
                )
              })}
            </div>
          )
        })}
      </nav>

      <div className="border-border-subtle mt-auto border-t pt-2.5">
        {collapsed ? (
          <span
            title={user ? `${user.name || user.email}` : undefined}
            className="border-border bg-bg-subtle text-2xs text-fg mx-auto grid size-8 place-items-center rounded-lg border font-semibold"
          >
            {initials(user)}
          </span>
        ) : (
          <div className="group hover:bg-bg-subtle flex items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors duration-100 ease-out">
            <span
              aria-hidden
              className="border-border bg-bg-subtle text-2xs text-fg grid size-8 shrink-0 place-items-center rounded-lg border font-semibold"
            >
              {initials(user)}
            </span>
            <div className="flex min-w-0 flex-col leading-tight">
              <span className="text-fg truncate text-sm font-medium">
                {user?.name || user?.email || 'Signed out'}
              </span>
              {user?.name && <span className="text-fg-subtle truncate text-xs">{user.email}</span>}
            </div>
            {/* Revealed on hover *and* on keyboard focus — a control that only
                appears under a cursor is one a keyboard user tabs into blind. */}
            <Button
              variant="ghost"
              size="icon"
              aria-label="Sign out"
              onClick={() => router.delete('/logout')}
              className={cn(
                'ml-auto size-7 rounded-md opacity-0 transition-opacity duration-100 ease-out',
                'group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100',
                'hover:bg-bg hover:text-err',
              )}
            >
              <LogOut className="size-3.5" />
            </Button>
          </div>
        )}
      </div>
    </aside>
  )
}
